import React, { useEffect, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  associateTemplateCategory,
  fetchCategoryDetails,
} from '~/redux/actions/admin-templates-actions';
import { getAllTemplates } from '~/redux/actions/template-builder';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE } from '~/utils/constants';

// Components
import MultiSelect from '~/components/General/MultiSelectNew';
import Loader from '~/components/General/Loader';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import { Box, InputLabel, CircularProgress } from '@mui/material';

const AssociateTemplatesModal = (props) => {
  const { openModal, handleCloseModal, selectedTemplateCategory, pagination } = props;
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [detailLoader, setDetailLoader] = useState(false);

  const dispatch = useDispatch();

  const templates = useSelector((state) => state.templateReducer.templates.rows);

  const templateOptions =
    templates.map((template) => {
      return {
        id: template.id,
        title: `ID:${template.id} ${template.title}`,
      };
    }) || [];

  const onClose = () => {
    handleCloseModal();
    setSelectedTemplates([]);
    setSearch('');
  };

  const handleAssociateTempsWithCategory = async (event) => {
    try {
      event.preventDefault();
      setLoading(true);
      const payload = {
        categoryId: selectedTemplateCategory?.id,
        templateIds: selectedTemplates,
      };
      const response = await dispatch(associateTemplateCategory(payload, pagination));
      if (response.status === 200) {
        dispatch(
          success(
            selectedTemplates?.length <= 0
              ? MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.DISASSOCIATE_MESSAGE
              : selectedTemplates?.length > 1
                ? MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.MULTI_SUCCESS_MESSAGE
                : MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.SUCCESS_MESSAGE
          )
        );
      } else {
        dispatch(success(response?.data?.message));
      }
      onClose();
    } catch (error) {
      dispatch(failure(error?.response?.data?.message));
      return error;
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  };

  const searching = async () => {
    setSearchLoading(true);
    await dispatch(
      getAllTemplates(
        DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.page,
        100,
        search?.includes('ID:') ? search?.replace(/ID:\d+\s*/, '') : search
      )
    );
    setSearchLoading(false);
  };

  const getSelectedCategoryDetail = async () => {
    try {
      setDetailLoader(true);
      const categoryDetail = await dispatch(
        fetchCategoryDetails(selectedTemplateCategory?.id, pagination)
      );
      if (categoryDetail.status === 200) {
        const existingTemplates = categoryDetail.data.data.templates.map((template) => {
          return template.id;
        });
        setSelectedTemplates(existingTemplates);
        setDetailLoader(false);
      }
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    searching();
  }, [search]);

  useEffect(() => {
    if (openModal) {
      getSelectedCategoryDetail();
    }
  }, [openModal]);

  return (
    <OlcFormModal
      open={openModal}
      onClose={onClose}
      title={MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.TITLE}
      className="olcAdminFormModal"
      footer={
        <>
          <OlcButton variant="secondary" type="button" onClick={onClose}>
            {MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton
            variant="primary"
            type="submit"
            form="associate-template-category-form"
            disabled={loading || detailLoader}
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '25px !important',
                  height: '25px !important',
                }}
              />
            ) : (
              MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.SUBMIT_BUTTON
            )}
          </OlcButton>
        </>
      }
    >
      {detailLoader ? (
        <Loader />
      ) : (
        <form
          id="associate-template-category-form"
          className="olcAdminFormFields"
          onSubmit={handleAssociateTempsWithCategory}
        >
          <Box className="olcAdminField">
            <InputLabel className="inputLabel">
              {MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.TEMPLATES_LABEL}
            </InputLabel>
            <MultiSelect
              options={templateOptions}
              selectedValue={selectedTemplates}
              setSelectedValue={setSelectedTemplates}
              component="multipleIds"
              placeHolderText={
                MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.TEMPLATES_PLACEHOLDER
              }
              searchFieldPlaceholderText="templates"
              searchAutocomplete
              onSearchChange={setSearch}
              searchLoading={searchLoading}
            />
          </Box>
        </form>
      )}
    </OlcFormModal>
  );
};

export default AssociateTemplatesModal;
